import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatFileSize } from "../../features/materials/utils/formatFileSize";
import {
  isLargeUpload,
  isTooLarge,
  isVideoType,
  materialContentTypeOf,
} from "../../features/materials/utils/materialUploadRules";
import { Group } from "../../features/members/models/Group";
import { Member } from "../../features/members/models/Member";
import MaterialAudiencePicker, { MaterialAudience } from "./MaterialAudiencePicker";

interface MaterialUploadDialogProps {
  file: File;
  categories: string[];
  /** Prefills the category with the currently active filter, when set. */
  initialCategory: string | null;
  members: Member[];
  groups: Group[];
  busy: boolean;
  /** The running upload's progress (0–1); null before the bytes start moving. */
  progress?: number | null;
  /** The last attempt did not finish; the dialog says so and lets the file be sent again. */
  failed?: boolean;
  onClose: () => void;
  onSubmit: (
    category: string,
    description: string,
    notifyMembers: boolean,
    audience: MaterialAudience,
  ) => void;
}

/**
 * Confirms an upload: category, description, audience, and the notify switch. Refuses a file above
 * its cap before anything is sent, hints at Wi-Fi for large ones and shows the progress while the
 * bytes go to the bucket.
 */
const MaterialUploadDialog: React.FC<MaterialUploadDialogProps> = ({
  file,
  categories,
  initialCategory,
  members,
  groups,
  busy,
  progress = null,
  failed = false,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [category, setCategory] = useState(initialCategory ?? "");
  const [description, setDescription] = useState("");
  const [notifyMembers, setNotifyMembers] = useState(false);
  const [audience, setAudience] = useState<MaterialAudience>({
    everyone: true,
    memberIds: [],
    groupIds: [],
  });

  const audienceValid =
    audience.everyone || audience.memberIds.length > 0 || audience.groupIds.length > 0;
  const tooLarge = isTooLarge(file);
  const isVideo = isVideoType(materialContentTypeOf(file));
  const progressLabel =
    progress === null ? "" : ` ${t("materials.upload.progress", { percent: Math.round(progress * 100) })}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/70">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-4 shadow-xl lg:p-6 dark:bg-primary-10">
        <h3 className="text-grey-10 dark:text-grey-90">
          {t("materials.uploadTitle", "Upload file")}
        </h3>
        <p className="mt-1 truncate text-sm text-grey-30 dark:text-grey-70">
          {[file.name, isVideo ? t("materials.upload.video") : null, formatFileSize(file.size)]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {tooLarge && <p className="field-error mt-2">{t("materials.upload.tooLarge")}</p>}
        {!tooLarge && isLargeUpload(file) && (
          <p className="mt-2 text-sm text-grey-40 dark:text-grey-80">{t("materials.upload.wifi")}</p>
        )}

        <div className="mt-4 space-y-3">
          <div>
            <label htmlFor="material-upload-category" className="mb-1 block text-sm text-grey-20 dark:text-grey-80">
              {t("materials.categoryLabel", "Category")}
            </label>
            <input
              id="material-upload-category"
              type="text"
              className="input"
              value={category}
              maxLength={100}
              list="material-upload-category-options"
              placeholder={t("materials.categoryPlaceholder", "e.g. Sheet music")}
              onChange={(e) => setCategory(e.target.value)}
              disabled={busy}
            />
            <datalist id="material-upload-category-options">
              {categories.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          </div>
          <div>
            <label htmlFor="material-upload-description" className="mb-1 block text-sm text-grey-20 dark:text-grey-80">
              {t("materials.descriptionLabel", "Description (optional)")}
            </label>
            <input
              id="material-upload-description"
              type="text"
              className="input"
              value={description}
              maxLength={500}
              onChange={(e) => setDescription(e.target.value)}
              disabled={busy}
            />
          </div>

          <MaterialAudiencePicker
            members={members}
            groups={groups}
            value={audience}
            disabled={busy}
            onChange={setAudience}
          />

          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={notifyMembers}
              onChange={(e) => setNotifyMembers(e.target.checked)}
              disabled={busy}
            />
            <span>
              <span className="block text-sm font-medium text-grey-20 dark:text-white">
                {t("materials.notifyMembers", "Notify members")}
              </span>
              <span className="block text-xs text-grey-40 dark:text-grey-80">
                {t("materials.notifyMembersHint", "Sends an announcement (opt-outs apply; only when the portal shows materials).")}
              </span>
            </span>
          </label>
        </div>

        {failed && !busy && <p className="field-error mt-4">{t("materials.upload.failed")}</p>}

        <div className="mt-5 flex justify-end gap-3">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onSubmit(category, description, notifyMembers, audience)}
            disabled={busy || !audienceValid || tooLarge}
          >
            {busy
              ? `${t("materials.uploading", "Uploading…")}${progressLabel}`
              : t("materials.uploadAction", "Upload file")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaterialUploadDialog;
